import type {IssueEventInfo} from '@github-ui/dotcom-schema/types/pull_requests/v1/timeline'
import {Link} from '@primer/react'
import {AddedToMergeQueueText} from '../issue-events/AddedToMergeQueueText'
import {AssignedText} from '../issue-events/AssignedText'
import {ClosedText} from '../issue-events/ClosedText'
import {ForcePushedText} from '../issue-events/ForcePushedText'
import {LabeledText} from '../issue-events/LabeledText'
import {LockedText} from '../issue-events/LockedText'
import {MergedText} from '../issue-events/MergedText'
import {RemovedFromMergeQueueText} from '../issue-events/RemovedFromMergeQueueText'
import {RenamedText} from '../issue-events/RenamedText'
import {ReviewDismissedText} from '../issue-events/ReviewDismissedText'
import {ReviewRequestText} from '../issue-events/ReviewRequestText'
import {ReferencedText} from '../issue-events/ReferencedText'
import {formatCopilotWorkSummaryText} from './format-copilot-work-summary-text'

const EVENT_TEXT: Partial<Record<string, string>> = {
  reopened: 'reopened this PR',
  unlocked: 'unlocked this conversation',
  convert_to_draft: 'marked this pull request as draft',
  ready_for_review: 'marked this pull request as ready for review',
  copilot_work_started: 'started a Copilot session',
}

const STACKED_AUTO_MERGE_EVENT_TEXT: Partial<Record<string, string>> = {
  auto_merge_enabled: 'enabled stacked auto-merge',
  auto_squash_enabled: 'enabled stacked auto-merge (squash)',
  auto_rebase_enabled: 'enabled stacked auto-merge (rebase)',
  auto_merge_disabled: 'disabled stacked auto-merge',
}

export function IssueEventText({info}: {info: IssueEventInfo}) {
  if (info.eventType === 'copilot_work_started' && info.copilotWorkSummary) {
    return info.sessionUrl ? (
      <Link inline href={info.sessionUrl} className="fgColor-default text-semibold">
        {formatCopilotWorkSummaryText(info.copilotWorkSummary)}
      </Link>
    ) : (
      formatCopilotWorkSummaryText(info.copilotWorkSummary)
    )
  }

  if (info.eventType === 'referenced') {
    return <ReferencedText info={info} />
  }

  return info.sessionUrl ? (
    <Link inline href={info.sessionUrl} className="fgColor-default text-semibold">
      <IssueEventDescription info={info} />
    </Link>
  ) : (
    <IssueEventDescription info={info} />
  )
}

function IssueEventDescription({info}: {info: IssueEventInfo}) {
  const {eventType} = info

  if (eventType === 'assigned' || eventType === 'unassigned') {
    return <AssignedText info={info} />
  }

  if (eventType === 'labeled' || eventType === 'unlabeled') {
    return <LabeledText info={info} />
  }

  if (eventType === 'review_requested' || eventType === 'review_request_removed') {
    return <ReviewRequestText info={info} />
  }

  if (eventType === 'merged') {
    return <MergedText info={info} />
  }

  if (eventType === 'head_ref_force_pushed' || eventType === 'base_ref_force_pushed') {
    return <ForcePushedText info={info} />
  }

  if (eventType === 'renamed') {
    return <RenamedText info={info} />
  }

  if (eventType === 'closed') {
    return <ClosedText info={info} />
  }

  if (eventType === 'locked') {
    return <LockedText info={info} />
  }

  if (eventType === 'review_dismissed') {
    return <ReviewDismissedText info={info} />
  }

  if (eventType === 'added_to_stack' || eventType === 'removed_from_stack') {
    const action = eventType === 'added_to_stack' ? 'added this pull request to' : 'removed this pull request from'
    const stack = info.stackNumber === undefined ? 'a stack' : `stack #${info.stackNumber}`
    return <>{`${action} ${stack}`}</>
  }

  if (eventType === 'added_to_merge_queue') {
    return <AddedToMergeQueueText info={info} />
  }

  if (eventType === 'removed_from_merge_queue') {
    return <RemovedFromMergeQueueText info={info} />
  }

  if (info.stackedAutoMerge && STACKED_AUTO_MERGE_EVENT_TEXT[eventType]) {
    return <>{STACKED_AUTO_MERGE_EVENT_TEXT[eventType]}</>
  }

  return <>{EVENT_TEXT[eventType] ?? eventType.replace(/_/g, ' ')}</>
}
